import {
  Actor,
  Animation,
  Collider,
  CollisionContact,
  CollisionType,
  Engine,
  Random,
  Side,
  SpriteSheet,
  vec,
} from 'excalibur';
import { CONFIG, LevelKey } from '../config';
import { Resources } from '../resources';
import { COLLISION_GROUPS } from '../collision-groups';

type Bounds = { left: number; right: number };

export class Elf extends Actor {
  private readonly spriteSheet = SpriteSheet.fromImageSource({
    image: Resources.Elf,
    grid: {
      rows: 1,
      columns: 4,
      spriteWidth: 16,
      spriteHeight: 16,
    },
  });
  private walkSpeed: number;
  private grounded = false;
  private roomBounds: Bounds;
  public working = false;
  random: Random;

  constructor(level: LevelKey, bounds: Bounds) {
    const { y } = CONFIG.levels[level].santa;
    super({
      name: 'Elf',
      width: 16,
      height: 16,
      pos: vec(0, y - 180),
      collisionType: CollisionType.Active,
      collisionGroup: COLLISION_GROUPS.elf,
      z: 9,
    });

    this.random = new Random();

    this.roomBounds = bounds;
    this.walkSpeed = this.random.integer(100, 150);
  }

  override onInitialize(_engine: Engine): void {
    this.body.useGravity = true;
    this.body.bounciness = 0.4;
    this.collider.useBoxCollider(16, 12, vec(0.5, 0.5), vec(0, -4));

    const padding = 40;
    this.pos.x = this.random.pickOne([
      this.random.integer(padding, this.roomBounds.left - padding),
      this.random.integer(this.roomBounds.right + padding, CONFIG.screen.width * CONFIG.scale - padding),
    ]);

    this.setupAnimations();

    const direction = Math.random() > 0.5 ? 1 : -1;
    this.vel.x = this.walkSpeed * direction;
  }

  private setupAnimations(): void {
    const walk = Animation.fromSpriteSheet(this.spriteSheet, [0, 1], 166);
    const work = Animation.fromSpriteSheet(this.spriteSheet, [2, 3], 166);

    this.graphics.add('walk', walk);
    this.graphics.add('work', work);
    this.graphics.use('walk');
  }

  override onPreUpdate(_engine: Engine): void {
    if (this.vel.y) {
      this.grounded = false;
    }

    const direction = Math.sign(this.vel.x) || 1;
    if (this.inRoom(this.roomBounds)) {
      this.vel.x = 0;
      this.graphics.use('work');
      this.working = true;
    } else {
      this.vel.x = this.walkSpeed * direction;
      this.graphics.use('walk');
      this.working = false;
    }

    this.graphics.flipHorizontal = direction < 0;
    this.keepInWorldBounds();
  }

  override onPostCollisionResolve(_self: Collider, _other: Collider, side: Side, _contact: CollisionContact): void {
    if (side === Side.Bottom) {
      this.grounded = true;
    }

    if (side === Side.Left || side === Side.Right) {
      this.vel.x = -this.vel.x;
    }
  }

  public inRoom(bounds: Bounds | undefined): boolean {
    if (!bounds) {
      return false;
    }

    return this.pos.x > bounds.left && this.pos.x < bounds.right && this.grounded;
  }

  private keepInWorldBounds(): void {
    const leftBound = 0;
    const rightBound = CONFIG.screen.width;

    if (this.pos.x < leftBound) {
      this.pos.x = leftBound;
      this.vel.x = Math.abs(this.walkSpeed);
    } else if (this.pos.x > rightBound) {
      this.pos.x = rightBound;
      this.vel.x = -Math.abs(this.walkSpeed);
    }
  }
}
